import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';

import { debounce } from 'lodash';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Popover,
  Button,
  Divider,
  FormControlLabel,
  Slider,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import '../App.css';

import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import logger from '../logger.js';
import GameLogModal from '../components/GameLogModal';
import OddsHistoryModal, { useOddsHistory } from '../components/OddsHistoryModal';
import { fetchDeepLink, openDeepLink } from '../deeplinks';
import { TRACKING_API_URL } from '../config';
// Tracking API base URL (ServerV2 REST endpoints)
// ══════════════════════════════════════════════════════════
//  CONSTANTS & MAPPINGS
// ══════════════════════════════════════════════════════════

// Map league strings to numeric IDs for game log API
const LEAGUE_TO_ID = { NFL: 1, NBA: 3 };
const GAME_LOG_LEAGUES = new Set(['NFL', 'NBA']);

// Bucket map for EV Age tint
const AGE_KEYFRAMES_ID = 'ev-age-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(AGE_KEYFRAMES_ID)) {
  const style = document.createElement('style');
  style.id = AGE_KEYFRAMES_ID;
  style.textContent = `
    @keyframes evAgeFade {
      0%   { background-color: rgba(0,255,0,0.75); }
      20%  { background-color: rgba(0,255,0,0.6); }
      40%  { background-color: rgba(0,255,0,0.45); }
      60%  { background-color: rgba(0,255,0,0.3); }
      80%  { background-color: rgba(0,255,0,0.15); }
      100% { background-color: transparent; }
    }
  `;
  document.head.appendChild(style);
}

const AGE_DURATION_SEC = 300;

function getAgeStyle(updatedAt) {
  if (!updatedAt) return {};
  const ageSec = Math.max(0, (Date.now() - new Date(updatedAt).getTime()) / 1000);
  if (ageSec >= AGE_DURATION_SEC) return { backgroundColor: 'transparent' };
  return {
    animation: `evAgeFade ${AGE_DURATION_SEC}s linear forwards`,
    animationDelay: `-${ageSec.toFixed(0)}s`,
  };
}

// Helper: derive expandMarketId (groups bets across sportsbooks and lines for the same market)
// Used for best odds highlighting, expansion panel, and hide-market logic
const deriveExpandMarketId = (bet) => {
  if (!bet.fixture_id || !bet.market_id) return null;
  // Player props: group by playerId (stable, line-independent)
  if (bet.playerId) {
    return `${bet.fixture_id}||${bet.market_id}||${bet.playerId}||${bet.side || ''}`;
  }
  // Game lines (spread/total/moneyline): group by fixture + market + side only
  // No grouping_key needed — side already differentiates Home/Away or Over/Under
  return `${bet.fixture_id}||${bet.market_id}||||${bet.side || ''}`;
};

// Helper: sort associated bets by best line/price
const sortAssociatedBets = (bets) => {
  if (!bets || bets.length === 0) return bets;
  const isOver = bets[0]?.side === 'Over';
  return [...bets].sort((a, b) => {
    const lineA = Number(a.points) || 0, lineB = Number(b.points) || 0;
    const priceA = a.price ?? -Infinity, priceB = b.price ?? -Infinity;
    if (lineA !== lineB) return isOver ? lineA - lineB : lineB - lineA;
    return priceB - priceA;
  });
};

// Helper: read from localStorage with type-aware defaults
const readStorage = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved === null || saved === 'null') return fallback;
    if (typeof fallback === 'number') return parseFloat(saved);
    if (typeof fallback === 'boolean' || Array.isArray(fallback)) return JSON.parse(saved);
    return saved;
  } catch { return fallback; }
};

// Helper: read nullable numeric from localStorage (returns null if not set)
const readStorageNullable = (key) => {
  const saved = localStorage.getItem(key);
  if (saved === null || saved === 'null' || saved === '') return null;
  const num = parseFloat(saved);
  return isNaN(num) ? null : num;
};

// ══════════════════════════════════════════════════════════
//  THEME
// ══════════════════════════════════════════════════════════

const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  components: {
    MuiInputLabel: { 
      styleOverrides: { 
        root: { 
          color: '#ffffff',
          fontSize: '0.875rem',
        } 
      } 
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' },
        },
      },
    },
    MuiMenu: { 
      styleOverrides: { 
        paper: { 
          backgroundColor: '#333333', 
          color: '#ffffff' 
        } 
      } 
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { 
        root: { 
          color: '#ffffff', 
          '&.Mui-checked': { color: '#ff1744' } 
        } 
      } 
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: { color: '#ff1744' },
        thumb: {
          backgroundColor: '#ff1744',
          '&:hover': { boxShadow: '0 0 0 8px rgba(255, 23, 68, 0.16)' },
        },
        track: { backgroundColor: '#ffffff' },
        rail: { backgroundColor: '#555555' },
        valueLabel: { backgroundColor: '#333333', color: '#ffffff', fontSize: '0.75rem' },
        markLabel: { color: '#ffffff', fontSize: '0.75rem' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundColor: '#1e1e1e', color: '#ffffff' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { backgroundColor: '#333333', color: '#ffffff' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#1e1e1e', color: '#ffffff' },
      },
    },
  },
});

// ══════════════════════════════════════════════════════════
//  HELPER: EV COLOR
// ══════════════════════════════════════════════════════════

const getEVColor = (value) => {
  if (value == null) return '#ffffff';
  const val = value * 100;
  let r, g, b;
  if (val <= -10) {
    r = 255; g = 0; b = 0;
  } else if (val > -10 && val <= 0) {
    const t = (val + 10) / 10;
    r = 255; g = Math.round(255 * t); b = Math.round(255 * t);
  } else if (val > 0 && val <= 10) {
    const t = val / 10;
    r = Math.round(255 * (1 - t)); g = 255; b = Math.round(255 * (1 - t));
  } else {
    r = 0; g = 255; b = 0;
  }
  return `rgb(${r},${g},${b})`;
};

// ══════════════════════════════════════════════════════════
//  COMMON STYLES
// ══════════════════════════════════════════════════════════

const CELL_STYLES = {
  extendedBackground: {
    width: 'calc(100% + 16px)',
    height: 'calc(100% + 16px)',
    margin: '-8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    pl: '16px',
  },
  cellText: {
    fontSize: '0.875rem',
    whiteSpace: 'pre',
  },
  eventText: {
    fontSize: '0.875rem',
    paddingLeft: '8px',
  },
  secondaryText: {
    fontSize: '0.875rem',
    paddingLeft: '8px',
    color: '#BBBBBB',
  },
};

// ══════════════════════════════════════════════════════════
//  STATIC COLUMN DEFINITIONS (field names match new data)
// ══════════════════════════════════════════════════════════

const STATIC_COLUMN_DEFINITIONS = [
  {
    field: 'eventName',
    headerName: 'Event',
    minWidth: 185,
    flex: 2,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
    sortComparator: (v1, v2, param1, param2) => {
      const date1 = param1.api.getCellValue(param1.id, 'eventStart');
      const date2 = param2.api.getCellValue(param2.id, 'eventStart');
      if (!date1 && !date2) return 0;
      if (!date1) return 1;
      if (!date2) return -1;
      return new Date(date1).getTime() - new Date(date2).getTime();
    },
  },
  {
    field: 'playerName',
    headerName: 'Bet Info',
    minWidth: 165,
    flex: 2,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
  {
    field: 'bookLogo',
    headerName: '',
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    resizable: false,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'price',
    headerName: 'Line/Odds',
    minWidth: 135,
    flex: 1,
    sortable: false,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
  {
    field: 'roi',
    headerName: 'EV',
    minWidth: 70,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
  {
    field: 'growthRate',
    headerName: 'Growth%',
    minWidth: 130,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
  {
    field: 'evUpdatedAt',
    headerName: 'EV Age',
    type: 'number',
    minWidth: 100,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
  {
    field: 'kelly',
    headerName: 'Kelly',
    minWidth: 70,
    sortable: true,
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    resizable: false,
  },
  {
    field: 'trackBet',
    headerName: 'Track',
    sortable: false,
    minWidth: 65,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
  {
    field: 'hideBet',
    headerName: 'Hide',
    sortable: false,
    minWidth: 25,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
  {
    field: 'deeplink',
    headerName: '',
    sortable: false,
    minWidth: 80,
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    resizable: false,
  },
];

// ══════════════════════════════════════════════════════════
//  HEADER RENDERERS
// ══════════════════════════════════════════════════════════

const HEADER_RENDERERS = {
  default: (text) => () => (
    <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
      {text}
    </Typography>
  ),
  withTooltip: (text, tooltip) => () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Tooltip title={tooltip}>
        <InfoIcon sx={{ fontSize: text === 'Growth%' ? '16px' : '0.875rem', color: '#ffffff', mr: 0.5 }} />
      </Tooltip>
      <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
        {text}
      </Typography>
    </Box>
  ),
};

// ══════════════════════════════════════════════════════════
//  MEMOIZED CELL COMPONENTS
// ══════════════════════════════════════════════════════════

const RoiCell = React.memo(({ value }) => {
  if (value == null) return '-';
  const formatted = (value * 100).toFixed(1).padStart(4, ' ') + '%';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ color: getEVColor(value), fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {formatted}
      </Typography>
    </Box>
  );
});

const GrowthRateCell = React.memo(({ value }) => {
  if (value == null) return '-';
  const val = value * 100;
  let r, g, b;
  if (val <= 0) { r = 255; g = 255; b = 255; }
  else if (val > 0 && val <= 0.5) { const t = val / 0.5; r = Math.round(204 * (1 - t)); g = 255; b = Math.round(204 * (1 - t)); }
  else { r = 0; g = 255; b = 0; }
  return <span style={{ color: `rgb(${r},${g},${b})` }}>{val.toFixed(2)}%</span>;
});

const KellyCell = React.memo(({ value, bankroll, kellyFraction }) => {
  const baseKellyValue = (value ?? 0) * bankroll;
  const adjustedKellyValue = baseKellyValue * (kellyFraction ?? 1);
  return adjustedKellyValue == null ? '' : `$${Math.round(adjustedKellyValue)}`;
});

const RoiAgeCell = React.memo(({ updatedAt }) => {
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => forceUpdate(v => v + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  if (!updatedAt) return null;
  const ageSec = Math.max(0, (Date.now() - new Date(updatedAt).getTime()) / 1000);
  const displayValue = `${Math.floor(ageSec / 60).toString().padStart(3, ' ')} min`;
  const ageStyle = getAgeStyle(updatedAt);

  return (
    <Box sx={{
      color: '#ffffff',
      borderRadius: 0,
      ...CELL_STYLES.extendedBackground,
      fontSize: '0.875rem',
    }} style={ageStyle}>
      <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre' }}>
        {displayValue}
      </Typography>
    </Box>
  );
});

// ══════════════════════════════════════════════════════════
//  DEEPLINK CELL — uses direct URLs from data, no API call
// ══════════════════════════════════════════════════════════

const DeeplinkCell = React.memo(({ row, isAuthenticated }) => {
  const hasDeeplink = row.hasDeeplink;
  const disabled = !hasDeeplink || !isAuthenticated;

  const handleBetClick = async () => {
    if (disabled) return;
    const url = await fetchDeepLink(row.id);
    if (url) openDeepLink(url);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'left', justifyContent: 'flex-start' }}>
      <Button
        onClick={handleBetClick}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? '#666666' : '#00ff00',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          '&.Mui-disabled': { color: '#666666' },
        }}
      >
        Bet
      </Button>
    </Box>
  );
});

// ══════════════════════════════════════════════════════════
//  TRACK & HIDE CELL COMPONENTS
// ══════════════════════════════════════════════════════════

const TrackBetCell = React.memo(({ row, trackedBets, toggleTrack, trackBet, untrackBet, trackingInProgress, immediatelyTracked, isAuthenticated }) => {
  const id = row.id;
  const isTracked = trackedBets.some(bet => bet.id === id) || immediatelyTracked.has(id);
  const isDisabled = !trackBet || !untrackBet || trackingInProgress.has(id) || !isAuthenticated;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => { if (!isDisabled) toggleTrack(row, trackBet, untrackBet); }}
        size="small"
        sx={{ padding: 0, color: isTracked ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isTracked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </IconButton>
    </Box>
  );
});

const HideBetCell = React.memo(({ row, hiddenBets, setHiddenBets, toggleHide, showHiddenBets, isAuthenticated, hideMarket, processedBets }) => {
  const id = row.id;
  const marketId = row.expandMarketId;

  let isHidden;
  if (hideMarket && marketId) {
    const marketBets = processedBets.filter(bet => bet.expandMarketId === marketId);
    isHidden = marketBets.some(bet => hiddenBets.includes(bet.id));
  } else {
    isHidden = hiddenBets.includes(id);
  }

  const isDisabled = !isAuthenticated;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleHide(row);
            if (hideMarket && marketId) {
              const marketBets = processedBets.filter(bet => bet.expandMarketId === marketId);
              const marketBetIds = marketBets.map(bet => bet.id);
              const updated = isHidden
                ? hiddenBets.filter(x => !marketBetIds.includes(x))
                : [...new Set([...hiddenBets, ...marketBetIds])];
              setHiddenBets(updated);
              localStorage.setItem('hiddenBets', JSON.stringify(updated));
            } else {
              const updated = isHidden
                ? hiddenBets.filter(x => x !== id)
                : [...hiddenBets, id];
              setHiddenBets(updated);
              localStorage.setItem('hiddenBets', JSON.stringify(updated));
            }
          }
        }}
        size="small"
        sx={{ padding: 0, color: isHidden ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={(isHidden && !showHiddenBets) || isDisabled}
      >
        {isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </Box>
  );
});

// ══════════════════════════════════════════════════════════
//  MOBILE BET CARD
// ══════════════════════════════════════════════════════════

const MobileBetCard = React.memo(({
  bet,
  bankroll,
  kellyFraction,
  trackedBets,
  hiddenSet,
  hiddenMarketSet,
  showHiddenBets,
  toggleTrack,
  toggleHide,
  trackBet,
  untrackBet,
  trackingInProgress,
  associatedCount,
  getAssociatedBets,
  isBestOdds,
  immediatelyTracked,
  isAuthenticated,
  bookLogos,
  teamMap,
  openOddsHistory,
  showVolume,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isTracked = trackedBets.some(tb => tb.id === bet.id) || immediatelyTracked.has(bet.id);
  const isHidden = hiddenSet.has(bet.id) || (bet.expandMarketId && hiddenMarketSet.has(bet.expandMarketId));
  const isTrackingDisabled = !trackBet || !untrackBet || trackingInProgress.has(bet.id) || !isAuthenticated;

  const kellyValue = bet.kelly ? Math.round(bet.kelly * bankroll * kellyFraction) : 0;
  const hasDeeplink = bet.hasDeeplink;

  const isTennis = bet.league === 'ATP' || bet.league === 'WTA';

  // Format event date
  const eventDate = bet.eventStart
    ? new Date(bet.eventStart).toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).replace(',', '')
    : '';

  return (
    <Card sx={{ mb: 1.5, border: '1px solid #333' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1}>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              {bet.marketCategory === 'Player Prop'
                ? (bet.playerName || '')
                : bet.market === 'point_spread'
                  ? (bet.side === 'Home' ? (teamMap[bet.homeTeamId]?.name || bet.selection) : (teamMap[bet.awayTeamId]?.name || bet.selection)) || ''
                  : bet.market === 'total_points' || bet.market?.includes('total')
                    ? `${teamMap[bet.awayTeamId]?.abbreviation || teamMap[bet.awayTeamId]?.name || ''}@${teamMap[bet.homeTeamId]?.abbreviation || teamMap[bet.homeTeamId]?.name || ''}`
                    : bet.selection || bet.playerName || ''}
            </Typography>
            <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.75rem' }}>
              {bet.betTypeDisplay}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => !isTrackingDisabled && toggleTrack(bet, trackBet, untrackBet)}
              disabled={isTrackingDisabled}
              sx={{
                p: 0.5,
                color: isTracked ? '#00ff00' : '#888',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                '&.Mui-disabled': { color: '#444' },
              }}
            >
              {isTracked ? <CheckBoxIcon sx={{ fontSize: 20 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => { if (isAuthenticated) toggleHide(bet); }}
              disabled={(isHidden && !showHiddenBets) || !isAuthenticated}
              sx={{
                p: 0.5,
                color: isHidden ? '#00ff00' : '#888',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                '&.Mui-disabled': { color: '#444' },
              }}
            >
              {isHidden ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Event Info */}
        <Box mb={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {bet.league && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem', mr: 0.5 }}>
                  {bet.league}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.875rem', mx: 0.5 }}>|</Typography>
              </>
            )}
            {isTennis ? (
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                {bet.eventName}
              </Typography>
            ) : (
              <>
                {teamMap[bet.awayTeamId]?.logo && (
                  <img src={teamMap[bet.awayTeamId]?.logo} alt="Away" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
                )}
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  {teamMap[bet.awayTeamId]?.abbreviation || teamMap[bet.awayTeamId]?.name || ''}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.75rem', mx: '2px' }}>@</Typography>
                {teamMap[bet.homeTeamId]?.logo && (
                  <img src={teamMap[bet.homeTeamId]?.logo} alt="Home" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
                )}
                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  {teamMap[bet.homeTeamId]?.abbreviation || teamMap[bet.homeTeamId]?.name || ''}
                </Typography>
              </>
            )}
          </Box>
          <Typography variant="body2" sx={{ color: '#BBB', fontSize: '0.75rem' }}>
            {eventDate}
          </Typography>
        </Box>

        {/* Book and Line with X More button */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {bookLogos[bet.bookName] && (
              <Tooltip title={bet.bookName || ''} placement="top" arrow>
                <img src={bookLogos[bet.bookName]} alt={bet.bookName} style={{ width: 48, height: 48, objectFit: 'contain' }} />
              </Tooltip>
            )}
            <Box sx={{
              backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
              px: 1, py: 0.5, borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255,255,255,0.05)' },
            }} onClick={() => openOddsHistory({
              fixture_id: bet.fixture_id,
              market_id: bet.market_id,
              selection: bet.selection,
              selection_line: bet.selection_line,
              player_id: bet.playerId || '',
              points: bet.points,
              playerName: bet.playerName || bet.selection || `${teamMap[bet.awayTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[0] || ''} @ ${teamMap[bet.homeTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[1] || ''}`,
              marketDisplay: bet.marketName || '',
              league: bet.league || '',
              awayTeam: teamMap[bet.awayTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[0] || '',
              homeTeam: teamMap[bet.homeTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[1] || '',
              awayTeamLogo: teamMap[bet.awayTeamId]?.logo || '',
              homeTeamLogo: teamMap[bet.homeTeamId]?.logo || '',
              eventStart: bet.eventStart,
              bookLogo: bookLogos[bet.bookName] || '',
            }, bet.bookName)}>
              <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {bet.sidepoints} {bet.price < 0 ? bet.price : `+${bet.price}`}
              </Typography>
              {showVolume && bet.limits?.max != null && (
                <Typography sx={{ color: '#888888', fontSize: '0.65rem', lineHeight: 1 }}>
                  {bet.limits.max >= 1000 ? `$${(bet.limits.max / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(bet.limits.max)}`}
                </Typography>
              )}
            </Box>
          </Stack>

          {associatedCount > 1 && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
              sx={{ minWidth: 'auto' }}
            >
              {associatedCount - 1} More
            </Button>
          )}
        </Stack>

        {/* Associated Bets Accordion */}
        {expanded && associatedCount > 1 && (
          <Box mb={1.5}>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem', mb: 1, display: 'block' }}>
              Other Books:
            </Typography>
            {(() => {
              const orderedBets = getAssociatedBets(bet);
              return orderedBets.map((assocBet, idx) => (
                <Stack
                  key={idx}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    mt: 1,
                    backgroundColor: assocBet.id === bet.id ? 'rgba(0, 150, 255, 0.2)' : 'transparent',
                    borderRadius: 1, px: 1, py: 0.5,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: assocBet.id === bet.id ? 'rgba(0, 150, 255, 0.3)' : 'rgba(255,255,255,0.05)' },
                  }}
                  onClick={() => openOddsHistory({
                    fixture_id: assocBet.fixture_id,
                    market_id: assocBet.market_id,
                    selection: assocBet.selection,
                    selection_line: assocBet.selection_line,
                    player_id: assocBet.playerId || '',
                    points: assocBet.points,
                    playerName: assocBet.playerName || assocBet.selection || `${teamMap[assocBet.awayTeamId]?.abbreviation || (assocBet.eventName || '').split(' @ ')[0] || ''} @ ${teamMap[assocBet.homeTeamId]?.abbreviation || (assocBet.eventName || '').split(' @ ')[1] || ''}`,
                    marketDisplay: assocBet.marketName || '',
                    league: assocBet.league || '',
                    awayTeam: teamMap[assocBet.awayTeamId]?.abbreviation || (assocBet.eventName || '').split(' @ ')[0] || '',
                    homeTeam: teamMap[assocBet.homeTeamId]?.abbreviation || (assocBet.eventName || '').split(' @ ')[1] || '',
                    awayTeamLogo: teamMap[assocBet.awayTeamId]?.logo || '',
                    homeTeamLogo: teamMap[assocBet.homeTeamId]?.logo || '',
                    eventStart: assocBet.eventStart,
                    bookLogo: bookLogos[assocBet.bookName] || '',
                  }, assocBet.bookName)}
                >
                  {bookLogos[assocBet.bookName] && (
                    <Tooltip title={assocBet.bookName || ''} placement="top" arrow>
                      <img src={bookLogos[assocBet.bookName]} alt={assocBet.bookName} style={{ width: 32, height: 32, objectFit: 'contain' }} />
                    </Tooltip>
                  )}
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {assocBet.sidepoints} {assocBet.price < 0 ? assocBet.price : `+${assocBet.price}`}
                  </Typography>
                </Stack>
              ));
            })()}
          </Box>
        )}

        {/* Key Metrics Row with Place Bet button */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Grid container spacing={2} sx={{ flex: 1 }}>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>EV</Typography>
              <Typography variant="body2" sx={{ color: getEVColor(bet.roi), fontWeight: 500, fontSize: '0.875rem' }}>
                {bet.roi ? `${(bet.roi * 100).toFixed(1)}%` : '-'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>Growth%</Typography>
              <Typography variant="body2" sx={{ color: bet.growthRate > 0 ? '#00ff00' : '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
                {bet.growthRate ? `${(bet.growthRate * 100).toFixed(2)}%` : '-'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>Kelly</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                ${kellyValue}
              </Typography>
            </Grid>
          </Grid>

          {/* Place Bet button */}
          <Button
            size="small"
            variant="contained"
            onClick={async () => {
              if (!hasDeeplink || !isAuthenticated) return;
              const url = await fetchDeepLink(bet.id);
              if (url) openDeepLink(url);
            }}
            disabled={!hasDeeplink || !isAuthenticated}
            sx={{
              backgroundColor: hasDeeplink && isAuthenticated ? '#00ff00' : '#333',
              color: hasDeeplink && isAuthenticated ? '#000' : '#666',
              minWidth: 'auto', px: 2, ml: 2,
              '&:hover': { backgroundColor: hasDeeplink && isAuthenticated ? '#00cc00' : '#333' },
              '&.Mui-disabled': { backgroundColor: '#333', color: '#666' },
            }}
          >
            Bet
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
});

// ══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════

const BetsDataGrid = ({ username, isAuthenticated }) => {
  const allBets = useStoreData('odds');
  const sportsbooks = useStoreData('sportsbooks');
  const pickemBooks = useMemo(() => new Set(
    (sportsbooks || []).filter(sb => sb.pickem).map(sb => sb.name)
  ), [sportsbooks]);
  const rawBets = useMemo(() => (allBets || []).filter(b => !pickemBooks.has(b.bookName)), [allBets, pickemBooks]);

  // ── Throttle bets updates: process new data at most every 2s ──
  // WebSocket pushes odds ticks constantly; without throttling, every tick
  // triggers the full useMemo chain + DataGridPro re-evaluates 38k+ rows.
  const THROTTLE_MS = 2000;
  const [bets, setBets] = useState(rawBets);
  const lastBetsUpdate = useRef(0);
  const pendingTimer = useRef(null);
  useEffect(() => {
    const now = Date.now();
    const elapsed = now - lastBetsUpdate.current;

    if (elapsed >= THROTTLE_MS) {
      // Enough time passed — update immediately
      setBets(rawBets);
      lastBetsUpdate.current = now;
    } else {
      // Schedule update for remaining time
      if (pendingTimer.current) clearTimeout(pendingTimer.current);
      pendingTimer.current = setTimeout(() => {
        setBets(rawBets);
        lastBetsUpdate.current = Date.now();
      }, THROTTLE_MS - elapsed);
    }

    return () => { if (pendingTimer.current) clearTimeout(pendingTimer.current); };
  }, [rawBets]);

  const marketsConfig = useStoreData('markets') || [];
  const leaguesConfig = useStoreData('leagues') || [];
  const { bookLogos, teamMap } = useLogoMaps();
  const apiRef = useGridApiRef();

  // ── State ──
  const [sortModel, setSortModel] = useState([{ field: 'growthRate', sort: 'desc' }]);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [bankroll, setBankroll] = useState(() => readStorage('bankroll', 1000));
  const [kellyFraction, setKellyFraction] = useState(() => readStorage('kellyFraction', 1));
  const [hideTracked, setHideTracked] = useState(() => readStorage('hideTracked', true));
  const [showHiddenBets, setShowHiddenBets] = useState(() => readStorage('showHiddenBets', false));
  const [hideMarket, setHideMarket] = useState(() => readStorage('hideMarket', false));
  const [showVolume, setShowVolume] = useState(() => readStorage('evtoolShowVolume', false));
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [snipeMinEV, setSnipeMinEV] = useState(() => readStorageNullable('snipeMinEV'));
  const [snipeMaxAge, setSnipeMaxAge] = useState(() => readStorageNullable('snipeMaxAge'));
  const [minEVDisplay, setMinEVDisplay] = useState('');
  const [maxAgeDisplay, setMaxAgeDisplay] = useState('');
  const [minGrowth, setMinGrowth] = useState(() => readStorageNullable('minGrowth'));
  const [minGrowthDisplay, setMinGrowthDisplay] = useState('');
  const [breakEvenWinRange, setBreakEvenWinRange] = useState(() => readStorage('breakEvenWinRange', [0, 100]));
  const [leagueFilter, setLeagueFilter] = useState(() => readStorage('leagueFilter', []));
  const [categoryFilter, setCategoryFilter] = useState(() => readStorage('categoryFilter', []));
  const [betTypeFilter, setBetTypeFilter] = useState(() => readStorage('betTypeFilter', []));
  const [bookFilter, setBookFilter] = useState(() => readStorage('bookFilter', []));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('dateRange');
    return saved ? (saved === 'null' ? null : Number(saved)) : 7;
  });
  const [hiddenBets, setHiddenBets] = useState(() => readStorage('hiddenBets', []).slice(-1000));
  const [hiddenMarketIds, setHiddenMarketIds] = useState(() => readStorage('hiddenMarketIds', []).slice(-1000));
  const [pendingRemoval, setPendingRemoval] = useState(new Set());
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [gridKey, setGridKey] = useState(0);

  // Debounced search: close expanded panels FIRST, then apply filter after delay
  // This prevents DataGrid-Pro's internal pipeline from crashing on stale row refs
  const debouncedSetSearchQuery = useMemo(
    () => debounce((value) => setSearchQuery(value), 250),
    []
  );
  useEffect(() => () => debouncedSetSearchQuery.cancel(), [debouncedSetSearchQuery]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);

    // If any detail panels are open, close them and remount grid BEFORE the filter fires
    if (expandedRowIdsRef.current.size > 0) {
      if (apiRef.current?.setExpandedDetailPanels) {
        try { apiRef.current.setExpandedDetailPanels(new Set()); } catch (_) { /* ignore */ }
      }
      setExpandedRowIds(new Set());
      expandedRowIdsRef.current = new Set();
      setGridKey(prev => prev + 1);
    }

    debouncedSetSearchQuery(value);
  }, [debouncedSetSearchQuery, apiRef]);

  const expandedRowIdsRef = useRef(new Set());

  useEffect(() => { expandedRowIdsRef.current = expandedRowIds; }, [expandedRowIds]);

  // Track previous filter values for synchronous change detection
  const prevFiltersRef = useRef({
    leagueFilter, categoryFilter, betTypeFilter, bookFilter, searchQuery,
    dateRange, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, minGrowth, breakEvenWinRange,
  });

  const filtersChanged =
    prevFiltersRef.current.leagueFilter !== leagueFilter ||
    prevFiltersRef.current.categoryFilter !== categoryFilter ||
    prevFiltersRef.current.betTypeFilter !== betTypeFilter ||
    prevFiltersRef.current.bookFilter !== bookFilter ||
    prevFiltersRef.current.searchQuery !== searchQuery ||
    prevFiltersRef.current.dateRange !== dateRange ||
    prevFiltersRef.current.hideTracked !== hideTracked ||
    prevFiltersRef.current.showHiddenBets !== showHiddenBets ||
    prevFiltersRef.current.snipeMinEV !== snipeMinEV ||
    prevFiltersRef.current.snipeMaxAge !== snipeMaxAge ||
    prevFiltersRef.current.minGrowth !== minGrowth ||
    prevFiltersRef.current.breakEvenWinRange !== breakEvenWinRange;

  if (filtersChanged) {
    expandedRowIdsRef.current = new Set();
    if (expandedRowIds.size > 0) {
      Promise.resolve().then(() => {
        setExpandedRowIds(new Set());
        setGridKey(prev => prev + 1);
      });
    }
    prevFiltersRef.current = {
      leagueFilter, categoryFilter, betTypeFilter, bookFilter, searchQuery,
      dateRange, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, minGrowth, breakEvenWinRange,
    };
  }

  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const [immediatelyTracked, setImmediatelyTracked] = useState(new Set());
  const [trackedBets, setTrackedBets] = useState([]);

  // ── Fetch tracked bets from REST API on mount ──
  useEffect(() => {
    if (!username || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${TRACKING_API_URL}?username=${encodeURIComponent(username)}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) {
          const bets = (data.tracked_bets || []).map(b => ({ ...b, id: b.odds_id }));
          setTrackedBets(bets);
        }
      } catch (err) {
        console.error('[TRACKED-BETS] Failed to fetch:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [username, isAuthenticated]);

  // ── REST-based track/untrack ──
  const apiTrackBet = useCallback(async (bet) => {
    // Read latest ref values to avoid stale closure
    // (teamMap/bookLogos may not be populated yet when this callback is first created)
    const currentTeamMap = teamMapRef.current || {};
    const currentBookLogos = bookLogosRef.current || {};

    // Build event name using abbreviations (matching arb/middle format)
    const awayLabel = currentTeamMap[bet.awayTeamId]?.abbreviation || currentTeamMap[bet.awayTeamId]?.name || '?';
    const homeLabel = currentTeamMap[bet.homeTeamId]?.abbreviation || currentTeamMap[bet.homeTeamId]?.name || '?';
    const eventName = `${awayLabel} @ ${homeLabel}`;

    // For game lines (no playerName), derive a meaningful display name
    // matching EVTool's own rendering logic
    let displayName = bet.playerName || '';
    if (!displayName) {
      if (bet.market === 'point_spread') {
        displayName = (bet.side === 'Home' ? (currentTeamMap[bet.homeTeamId]?.name || bet.selection) : (currentTeamMap[bet.awayTeamId]?.name || bet.selection)) || '';
      } else if (bet.market === 'total_points' || bet.market?.includes('total')) {
        displayName = `${awayLabel}@${homeLabel}`;
      } else {
        displayName = bet.selection || '';
      }
    }

    const body = {
      username,
      odds_id:         bet.id,
      sportsbook:      bet.bookName,
      sportsbook_logo: currentBookLogos[bet.bookName] || '',
      fixture_id:      bet.fixture_id,
      event_name:      eventName,
      event_start:     bet.eventStart,
      league:          bet.league,
      player_name:     displayName,
      player_id:       bet.playerId,
      market_id:       bet.market_id,
      market_display:  bet.betTypeDisplay,
      side:            bet.side,
      points:          bet.points,
      price:           bet.price,
      roi:             bet.roi,
      home_team:       currentTeamMap[bet.homeTeamId]?.name || '',
      away_team:       currentTeamMap[bet.awayTeamId]?.name || '',
      home_logo:       currentTeamMap[bet.homeTeamId]?.logo || '',
      away_logo:       currentTeamMap[bet.awayTeamId]?.logo || '',
      source_type:     'evtool',
    };
    const resp = await fetch(TRACKING_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok && resp.status !== 409) throw new Error(`Track failed: HTTP ${resp.status}`);
    setTrackedBets(prev => [...prev, { ...body, id: bet.id }]);
  }, [username]);

  const apiUntrackBet = useCallback(async (betId) => {
    const resp = await fetch(
      `${TRACKING_API_URL}/${encodeURIComponent(betId)}?username=${encodeURIComponent(username)}`,
      { method: 'DELETE' }
    );
    if (!resp.ok && resp.status !== 404) throw new Error(`Untrack failed: HTTP ${resp.status}`);
    setTrackedBets(prev => prev.filter(b => b.id !== betId));
  }, [username]);
  const [gameLogModalOpen, setGameLogModalOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);
  const [gameLogData, setGameLogData] = useState(null);
  const [gameLogLoading, setGameLogLoading] = useState(false);
  const [gameLogSport, setGameLogSport] = useState('NFL');
  const { historyOpen, historyData, historyLoading, historyMeta, openOddsHistory, closeOddsHistory } = useOddsHistory();
  const gridContainerRef = useRef(null);
  const filterRef = useRef(null);
  const filtersInitialized = useRef(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isMobile = screenWidth < 900;
  // ── Refs for immediate state access ──
  const trackingStateRef = useRef(new Set());
  const trackedBetsRef = useRef([]);

  useEffect(() => {
    trackedBetsRef.current = trackedBets;
    const currentTrackedIds = new Set(trackedBets.map(bet => bet.id));
    setImmediatelyTracked(prev => {
      const filtered = new Set([...prev].filter(id => !currentTrackedIds.has(id)));
      return filtered.size !== prev.size ? filtered : prev;
    });
  }, [trackedBets]);

  useEffect(() => { trackingStateRef.current = trackingInProgress; }, [trackingInProgress]);

  // ── Scroll blocking for unauthenticated users ──
  useEffect(() => {
    const gridContainer = gridContainerRef.current;
    if (!isAuthenticated && gridContainer) {
      const preventScroll = (e) => { e.preventDefault(); e.stopPropagation(); };
      gridContainer.addEventListener('wheel', preventScroll, { passive: false });
      gridContainer.addEventListener('scroll', preventScroll, { passive: false });
      gridContainer.addEventListener('touchmove', preventScroll, { passive: false });
      return () => {
        gridContainer.removeEventListener('wheel', preventScroll);
        gridContainer.removeEventListener('scroll', preventScroll);
        gridContainer.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [isAuthenticated]);

  // ── Persist all settings to localStorage in one effect ──
  useEffect(() => {
    const saves = {
      bankroll: bankroll.toString(), kellyFraction: kellyFraction.toString(),
      hideTracked: JSON.stringify(hideTracked), showHiddenBets: JSON.stringify(showHiddenBets),
      hiddenMarketIds: JSON.stringify(hiddenMarketIds), hideMarket: JSON.stringify(hideMarket),
      evtoolShowVolume: JSON.stringify(showVolume),
      snipeMinEV: JSON.stringify(snipeMinEV), minGrowth: JSON.stringify(minGrowth),
      snipeMaxAge: JSON.stringify(snipeMaxAge), breakEvenWinRange: JSON.stringify(breakEvenWinRange),
      leagueFilter: JSON.stringify(leagueFilter), categoryFilter: JSON.stringify(categoryFilter),
      betTypeFilter: JSON.stringify(betTypeFilter), bookFilter: JSON.stringify(bookFilter),
      dateRange: JSON.stringify(dateRange),
    };
    Object.entries(saves).forEach(([k, v]) => localStorage.setItem(k, v));
  }, [bankroll, kellyFraction, hideTracked, showHiddenBets, hiddenMarketIds, hideMarket,
      snipeMinEV, minGrowth, snipeMaxAge, breakEvenWinRange, leagueFilter, categoryFilter,
      betTypeFilter, bookFilter, dateRange, showVolume]);

  useEffect(() => { setMinEVDisplay(snipeMinEV ?? 'No Min'); }, [snipeMinEV]);
  useEffect(() => { setMinGrowthDisplay(minGrowth ?? 'No Min'); }, [minGrowth]);
  useEffect(() => { setMaxAgeDisplay(snipeMaxAge ?? 'No Max'); }, [snipeMaxAge]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ══════════════════════════════════════════════════════════
  //  PROCESS BETS — filter EV subset & derive fields
  // ══════════════════════════════════════════════════════════

  const processedBets = useMemo(() => {
    if (!Array.isArray(bets)) return [];

    const result = bets
      .filter(bet => {
        if (bet.roi == null) return false;
        return true;
      })
      .map((row) => {
        // Derive display fields on existing object (no clone)
        if (!row.expandMarketId) {
          row.expandMarketId = deriveExpandMarketId(row);
        }
        if (!row.betTypeDisplay) {
          row.betTypeDisplay = row.marketName || row.market;
        }

        // Derive sidepoints display
        let sideLabel = row.side || '';
        const isSpread = sideLabel === 'Home' || sideLabel === 'Away';
        if (sideLabel === 'Over') sideLabel = 'o';
        else if (sideLabel === 'Under') sideLabel = 'u';
        else if (isSpread || sideLabel === 'Draw') sideLabel = '';
        row.sideLabel = sideLabel;

        // Build sidepoints string
        if (row.points != null) {
          const ptsNum = Number(row.points);
          const ptsStr = Number.isInteger(ptsNum) ? `${ptsNum}.0` : String(ptsNum);
          if (isSpread) {
            // Spread: show signed number (e.g. "+15.5", "-6.0")
            row.sidepoints = ptsNum > 0 ? `+${ptsStr}` : ptsStr;
          } else {
            // Over/Under: prefix + number (e.g. "o222.5", "u1.5")
            row.sidepoints = `${sideLabel}${ptsStr}`;
          }
        } else {
          row.sidepoints = sideLabel;
        }

        return row;
      });

    return result;
  }, [bets]);
  const safeguardedTrackBet = useCallback((bet) => {
    if (!isAuthenticated || !username) return;
    if (trackedBetsRef.current.some(existing => existing.id === bet.id)) return;
    setImmediatelyTracked(prev => new Set(prev).add(bet.id));
    apiTrackBet(bet).catch(err => {
      console.error('[TRACKED-BETS] Track failed:', err.message);
      setImmediatelyTracked(prev => { const next = new Set(prev); next.delete(bet.id); return next; });
    });
  }, [apiTrackBet, isAuthenticated, username]);

  const safeguardedUntrackBet = useCallback((betId) => {
    if (!isAuthenticated || !username) return;
    if (!trackedBetsRef.current.some(bet => bet.id === betId)) return;
    setImmediatelyTracked(prev => { const next = new Set(prev); next.delete(betId); return next; });
    apiUntrackBet(betId).catch(err => {
      console.error('[TRACKED-BETS] Untrack failed:', err.message);
    });
  }, [apiUntrackBet, isAuthenticated, username]);

  // Helper: close a detail panel for a given row
  const closeDetailPanel = useCallback((id) => {
    if (apiRef.current?.setExpandedDetailPanels) {
      const current = apiRef.current.getExpandedDetailPanels();
      if (current?.has?.(id)) {
        const next = new Set(current);
        next.delete(id);
        apiRef.current.setExpandedDetailPanels(next);
      }
    }
    if (expandedRowIds.has(id)) {
      setExpandedRowIds(new Set([...expandedRowIds].filter(rid => rid !== id)));
    }
  }, [expandedRowIds, apiRef]);

  const toggleTrack = useCallback((row, trackBetFunc, untrackBetFunc) => {
    if (!isAuthenticated) return;
    const id = row.id;
    const marketId = row.expandMarketId;
    if (trackingStateRef.current.has(id)) return;

    closeDetailPanel(id);

    const newTrackingState = new Set(trackingStateRef.current);
    newTrackingState.add(id);
    trackingStateRef.current = newTrackingState;
    setTrackingInProgress(newTrackingState);

    const alreadyTracked = trackedBetsRef.current.some(bet => bet.id === id);

    if (alreadyTracked) {
      safeguardedUntrackBet(id);
    } else {
      safeguardedTrackBet(row);

      if (hideTracked) {
        if (hideMarket && marketId) {
          const marketBets = processedBets.filter(bet => bet.expandMarketId === marketId && bet.id !== id);
          const marketBetIds = marketBets.map(bet => bet.id);
          if (marketBetIds.length > 0) {
            setHiddenBets(prev => {
              const newHiddenBets = [...new Set([...prev, ...marketBetIds])];
              localStorage.setItem('hiddenBets', JSON.stringify(newHiddenBets));
              return newHiddenBets;
            });
            if (!showHiddenBets) {
              marketBets.forEach(bet => {
                setPendingRemoval(prev => new Set(prev).add(bet.id));
              });
              setTimeout(() => {
                marketBets.forEach(bet => {
                  setPendingRemoval(prev => { const next = new Set(prev); next.delete(bet.id); return next; });
                });
              }, 200);
            }
          }
        }
        setPendingRemoval(prev => new Set(prev).add(id));
        setTimeout(() => {
          setPendingRemoval(prev => { const next = new Set(prev); next.delete(id); return next; });
        }, 200);
      }
    }

    setTimeout(() => {
      const clearedState = new Set(trackingStateRef.current);
      clearedState.delete(id);
      trackingStateRef.current = clearedState;
      setTrackingInProgress(clearedState);
    }, 500);
  }, [safeguardedTrackBet, safeguardedUntrackBet, hideTracked, isAuthenticated, hideMarket, processedBets, showHiddenBets, closeDetailPanel]);

  const debouncedToggleTrack = useCallback(
    debounce(toggleTrack, 100, { leading: true, trailing: false, maxWait: 200 }),
    [toggleTrack]
  );

  const toggleHide = useCallback((row) => {
    if (!isAuthenticated) return;
    const id = row.id;
    const marketId = row.expandMarketId;

    closeDetailPanel(id);

    if (hideMarket && marketId) {
      const isMarketHidden = hiddenMarketIds.includes(marketId);
      if (isMarketHidden) {
        const updated = hiddenMarketIds.filter(mId => mId !== marketId);
        setHiddenMarketIds(updated);
        localStorage.setItem('hiddenMarketIds', JSON.stringify(updated));
      } else {
        const newHiddenMarketIds = [...hiddenMarketIds, marketId];
        setHiddenMarketIds(newHiddenMarketIds);
        localStorage.setItem('hiddenMarketIds', JSON.stringify(newHiddenMarketIds));
        if (!showHiddenBets) {
          const marketBets = processedBets.filter(bet => bet.expandMarketId === marketId);
          marketBets.forEach(bet => {
            setPendingRemoval(prev => new Set(prev).add(bet.id));
            setTimeout(() => {
              setPendingRemoval(prev => { const next = new Set(prev); next.delete(bet.id); return next; });
            }, 200);
          });
        }
      }
    } else {
      const isHidden = hiddenBets.includes(id);
      if (!isHidden && !showHiddenBets) {
        setPendingRemoval(prev => new Set(prev).add(id));
        setTimeout(() => {
          setPendingRemoval(prev => { const next = new Set(prev); next.delete(id); return next; });
        }, 200);
      }
    }
  }, [hiddenBets, hiddenMarketIds, showHiddenBets, isAuthenticated, hideMarket, processedBets, closeDetailPanel]);

  // ══════════════════════════════════════════════════════════
  //  FILTER OPTIONS & FILTERING
  // ══════════════════════════════════════════════════════════

  const filterOptions = useMemo(() => {
    // ── Leagues: from server-provided leaguesConfig (active leagues from DB) ──
    const leagues = leaguesConfig.length > 0
      ? leaguesConfig.map(l => l.name || l.id).filter(Boolean)
      : [...new Set(processedBets.map(b => b.league).filter(Boolean))];

    // ── Categories: still derived from data (not tracked in DB config) ──
    const categories = [...new Set(processedBets.map(b => b.marketCategory).filter(Boolean))];

    // ── Bet Types: from server-provided marketsConfig (ingest=1 markets from DB) ──
    const betTypes = marketsConfig.length > 0
      ? marketsConfig.map(m => m.name || m.id).filter(Boolean)
      : [...new Set(processedBets.map(b => b.betTypeDisplay).filter(Boolean))];

    // ── Books: from server-provided sportsbooks config (ingest=1 from DB) ──
    const books = sportsbooks.length > 0
      ? sportsbooks.filter(sb => !sb.pickem).map(sb => sb.name).filter(Boolean)
      : [...new Set(processedBets.map(b => b.bookName).filter(Boolean))];

    // Preserve currently selected filter values that may not be in config yet
    leagueFilter.forEach(item => { if (!leagues.includes(item)) leagues.push(item); });
    categoryFilter.forEach(item => { if (!categories.includes(item)) categories.push(item); });
    betTypeFilter.forEach(item => { if (!betTypes.includes(item)) betTypes.push(item); });
    bookFilter.forEach(item => { if (!books.includes(item)) books.push(item); });

    return {
      leagues: leagues.sort(),
      categories: categories.sort(),
      betTypes: betTypes.sort(),
      books: books.sort(),
    };
  }, [leaguesConfig, marketsConfig, sportsbooks, processedBets, leagueFilter, categoryFilter, betTypeFilter, bookFilter]);

  const filterData = useMemo(() => ({
    trackedSet: new Set(trackedBets.map(bet => bet.id)),
    trackedCompoundSet: new Set(trackedBets.map(tb => `${tb.fixture_id || ''}|${tb.sportsbook || ''}|${tb.player_id || ''}|${tb.market_id || ''}|${(tb.side || '').toLowerCase()}`)),
    immediatelyTrackedSet: new Set(immediatelyTracked),
    hiddenSet: new Set(hiddenBets),
    hiddenMarketSet: new Set(hiddenMarketIds),
    pendingRemovalSet: new Set(pendingRemoval),
    leagueSet: new Set(leagueFilter),
    categorySet: new Set(categoryFilter),
    betTypeSet: new Set(betTypeFilter),
    bookSet: new Set(bookFilter),
    searchTerms: searchQuery.toLowerCase().split(/\s+/).filter(term => term),
    hasLeagueFilter: leagueFilter.length > 0,
    hasCategoryFilter: categoryFilter.length > 0,
    hasBetTypeFilter: betTypeFilter.length > 0,
    hasBookFilter: bookFilter.length > 0,
    hasSearchFilter: searchQuery.trim().length > 0,
  }), [trackedBets, immediatelyTracked, hiddenBets, hiddenMarketIds, pendingRemoval, leagueFilter, categoryFilter, betTypeFilter, bookFilter, searchQuery]);

  const filteredBets = useMemo(() => {
    if (!processedBets.length) return [];

    const {
      trackedSet, trackedCompoundSet, immediatelyTrackedSet, hiddenSet, hiddenMarketSet, pendingRemovalSet,
      leagueSet, categorySet, betTypeSet, bookSet, searchTerms,
      hasLeagueFilter, hasCategoryFilter, hasBetTypeFilter, hasBookFilter, hasSearchFilter,
    } = filterData;

    return processedBets.filter(bet => {
      // Tracked filter (by odds_id OR compound key for cross-line matches)
      if (hideTracked && !pendingRemovalSet.has(bet.id)) {
        if (trackedSet.has(bet.id) || immediatelyTrackedSet.has(bet.id)) return false;
        const compoundKey = `${bet.fixture_id || ''}|${bet.bookName || ''}|${bet.playerId || ''}|${bet.market_id || ''}|${(bet.selection_line || bet.side || '').toLowerCase()}`;
        if (trackedCompoundSet.has(compoundKey)) return false;
      }

      // Hidden filter
      if (!showHiddenBets && !pendingRemovalSet.has(bet.id)) {
        if (hiddenSet.has(bet.id) || (bet.expandMarketId && hiddenMarketSet.has(bet.expandMarketId))) return false;
      }

      // Dropdown filters
      if (hasLeagueFilter && bet.league && !leagueSet.has(bet.league)) return false;
      if (hasCategoryFilter && bet.marketCategory && !categorySet.has(bet.marketCategory)) return false;
      if (hasBetTypeFilter && bet.betTypeDisplay && !betTypeSet.has(bet.betTypeDisplay)) return false;
      if (hasBookFilter && bet.bookName && !bookSet.has(bet.bookName)) return false;

      // Numeric filters
      if (snipeMinEV !== null && (bet.roi == null || bet.roi <= snipeMinEV / 100)) return false;
      if (minGrowth !== null && (bet.growthRate == null || bet.growthRate <= minGrowth / 100)) return false;

      if (snipeMaxAge !== null && (bet.evUpdatedAt || bet.updatedAt)) {
        const ageMinutes = (Date.now() - new Date(bet.evUpdatedAt || bet.updatedAt).getTime()) / 60000;
        if (ageMinutes >= snipeMaxAge) return false;
      }

      if (dateRange !== null && bet.eventStart) {
        const maxTime = Date.now() + dateRange * 24 * 60 * 60 * 1000;
        if (new Date(bet.eventStart).getTime() > maxTime) return false;
      }

      if (bet.breakEvenWin !== null && bet.breakEvenWin !== undefined) {
        const breakEvenPercent = bet.breakEvenWin * 100;
        if (breakEvenPercent < breakEvenWinRange[0] || breakEvenPercent > breakEvenWinRange[1]) return false;
      }

      // Search filter (expensive — last)
      if (hasSearchFilter && searchTerms.length > 0) {
        const searchableText = `${bet.eventName || ''} ${bet.playerName || ''} ${bet.betTypeDisplay || ''} ${bet.bookName || ''}`.toLowerCase();
        for (const term of searchTerms) {
          if (!searchableText.includes(term)) return false;
        }
      }

      return true;
    });
  }, [processedBets, filterData, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, dateRange, breakEvenWinRange, minGrowth]);

  // Diagnostic removed — was firing on every odds tick

  // Clear expanded rows on filter change
  useEffect(() => {
    if (expandedRowIds.size > 0) {
      if (apiRef.current?.setExpandedDetailPanels) {
        apiRef.current.setExpandedDetailPanels(new Set());
      }
      setExpandedRowIds(new Set());
    }
  }, [leagueFilter, categoryFilter, betTypeFilter, bookFilter, searchQuery, dateRange, breakEvenWinRange, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, minGrowth, apiRef]);

  const displayBets = filteredBets;

  // ══════════════════════════════════════════════════════════
  //  MARKET METADATA + GROUPED BETS (no sorting on build)
  // ══════════════════════════════════════════════════════════

  // Single pass: group bets by expandMarketId AND compute best odds inline.
  // Groups are pre-sorted so getDetailPanelContent (called per-row by DataGridPro) does zero work.
  const { marketMeta, groupedBets } = useMemo(() => {
    const meta = new Map();   // expandMarketId -> { count, bestOdds, bestLine, side }
    const groups = new Map();  // expandMarketId -> bet[] (pre-sorted)

    processedBets.forEach((bet) => {
      if (!bet.expandMarketId) return;
      const line = Number(bet.points) || 0;
      const price = bet.price ?? -Infinity;

      // Build group array (O(1) push)
      let group = groups.get(bet.expandMarketId);
      if (!group) { group = []; groups.set(bet.expandMarketId, group); }
      group.push(bet);

      // Track best odds inline (no sort needed)
      if (!meta.has(bet.expandMarketId)) {
        meta.set(bet.expandMarketId, { count: 1, bestOdds: price, bestLine: line, side: bet.side });
        return;
      }

      const m = meta.get(bet.expandMarketId);
      m.count += 1;

      const isOver = m.side === 'Over';
      const lineBetter = isOver ? (line < m.bestLine) : (line > m.bestLine);
      if (lineBetter || (line === m.bestLine && price > m.bestOdds)) {
        m.bestOdds = price;
        m.bestLine = line;
      }
    });

    // Pre-sort only multi-bet groups (one-time cost)
    groups.forEach((group, key) => {
      if (group.length > 1) {
        groups.set(key, sortAssociatedBets(group));
      }
    });

    // Stamp marketCount on each bet so getDetailPanelHeight can read it
    // directly from the row with zero external deps
    groups.forEach((group) => {
      const count = group.length;
      group.forEach(bet => { bet.marketCount = count; });
    });

    return { marketMeta: meta, groupedBets: groups };
  }, [processedBets]);

  // Refs so detail panel callbacks have stable [] deps and DataGridPro
  // doesn't re-evaluate 38k rows on every WebSocket tick
  const groupedBetsRef = useRef(groupedBets);
  groupedBetsRef.current = groupedBets;
  const bookLogosRef = useRef(bookLogos);
  bookLogosRef.current = bookLogos;
  const teamMapRef = useRef(teamMap);
  teamMapRef.current = teamMap;
  const openOddsHistoryRef = useRef(openOddsHistory);
  openOddsHistoryRef.current = openOddsHistory;

  // Groups are pre-sorted in useMemo — just return directly
  const getAssociatedBets = useCallback((row) => {
    if (!row || !row.expandMarketId) return [];
    const group = groupedBets.get(row.expandMarketId);
    if (!group || group.length <= 1) return [];
    return group;
  }, [groupedBets]);

  // ══════════════════════════════════════════════════════════
  //  INITIALIZE FILTERS
  // ══════════════════════════════════════════════════════════

  useEffect(() => {
    if (filterOptions.leagues.length > 0 &&
        filterOptions.categories.length > 0 &&
        filterOptions.betTypes.length > 0 &&
        filterOptions.books.length > 0 &&
        !filtersInitialized.current) {

      // Validate saved filters: if no overlap with current data, treat as stale
      const validateFilter = (key, options) => {
        const saved = readStorage(key, []);
        const valid = saved.filter(v => new Set(options).has(v));
        return valid.length > 0 ? valid : null; // null = use defaults
      };

      const defaultBooks = () => filterOptions.books.filter(b => !['prophetx', 'novig'].includes(b.toLowerCase()));

      setLeagueFilter(validateFilter('leagueFilter', filterOptions.leagues) || filterOptions.leagues);
      setCategoryFilter(validateFilter('categoryFilter', filterOptions.categories) || filterOptions.categories);
      setBetTypeFilter(validateFilter('betTypeFilter', filterOptions.betTypes) || filterOptions.betTypes);
      setBookFilter(validateFilter('bookFilter', filterOptions.books) || defaultBooks());

      filtersInitialized.current = true;
    }
  }, [filterOptions]);

  useEffect(() => {
    setCurrentPage(0);
  }, [leagueFilter, categoryFilter, betTypeFilter, bookFilter, hideTracked, showHiddenBets, snipeMinEV, snipeMaxAge, searchQuery, dateRange, breakEvenWinRange]);

  // ── Filter handlers ──
  const handleFilterChange = useCallback((e, options, setter, debouncedSetter) => {
    const val = e.target.value;

    // CRITICAL: If panels are open, close them and force grid remount BEFORE filter changes rows
    if (expandedRowIdsRef.current.size > 0) {
      expandedRowIdsRef.current = new Set();
      setExpandedRowIds(new Set());
      setGridKey(prev => prev + 1);
      if (apiRef.current?.setExpandedDetailPanels) {
        try { apiRef.current.setExpandedDetailPanels(new Set()); } catch (error) { /* ignore */ }
      }
    }

    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        if (debouncedSetter) debouncedSetter(newValue);
        return newValue;
      });
    } else {
      if (debouncedSetter) debouncedSetter(val);
      else setter(val);
    }
  }, [apiRef]);

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
  };

  // ══════════════════════════════════════════════════════════
  //  CSV EXPORT
  // ══════════════════════════════════════════════════════════

  const exportToCSV = useCallback(() => {
    if (!filteredBets.length || !isAuthenticated) return;

    const headers = [
      'ID', 'League', 'Event', 'Event Start', 'Participant', 'Bet Type',
      'Book', 'Line', 'Odds', 'Implied%', 'Model%', 'Growth%', 'EV', 'Kelly', 'EV Age',
    ];

    const rows = filteredBets.map(bet => {
      const eventStart = bet.eventStart
        ? new Date(bet.eventStart).toLocaleString('en-US', {
            timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: 'numeric', minute: '2-digit', hour12: true,
          }).replace(',', '')
        : '';
      const odds = bet.price == null ? '' : bet.price < 0 ? bet.price : `+${bet.price}`;
      const implied = bet.breakEvenWin == null ? '' : `${(bet.breakEvenWin * 100).toFixed(1)}%`;
      const model = bet.trueWin == null ? '' : `${(bet.trueWin * 100).toFixed(1)}%`;
      const growth = bet.growthRate == null ? '' : `${(bet.growthRate * 100).toFixed(2)}%`;
      const ev = bet.roi == null ? '' : `${(bet.roi * 100).toFixed(1)}%`;
      const kelly = bet.kelly == null ? '' : `$${(bet.kelly * bankroll * kellyFraction).toFixed(2)}`;
      const evAge = (bet.evUpdatedAt || bet.updatedAt)
        ? `${Math.floor((Date.now() - new Date(bet.evUpdatedAt || bet.updatedAt).getTime()) / 1000 / 60)} min`
        : '';

      return [
        bet.id, bet.league || '', bet.eventName || '', eventStart,
        bet.playerName || '', bet.betTypeDisplay || '', bet.bookName || '',
        bet.sidepoints || '', odds, implied, model, growth, ev, kelly, evAge,
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `bets_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredBets, bankroll, kellyFraction, isAuthenticated]);

  // ── Settings handlers ──
  const handleSettingsOpen = useCallback((event) => setSettingsAnchorEl(event.currentTarget), []);
  const handleSettingsClose = useCallback(() => setSettingsAnchorEl(null), []);
  const handleSliderChange = useCallback((event, newValue) => {
    const [min, max] = newValue;
    setBreakEvenWinRange([Math.min(min, max), Math.max(min, max)]);
  }, []);

  // ══════════════════════════════════════════════════════════
  //  GAME LOG MODAL
  // ══════════════════════════════════════════════════════════

  const handlePlayerClick = useCallback(async (betRow) => {
    const league = betRow.league;
    if (!GAME_LOG_LEAGUES.has(league)) return;

    const playerId = betRow.playerId;
    if (!playerId) return;

    const apiPath = league.toLowerCase();
    setGameLogSport(league);
    setGameLogLoading(true);

    try {
      const url = `https://ws.betpros.ai:8080/api/player-game-logs/${apiPath}/${playerId}`;
      const response = await fetch(url);
      if (!response.ok) { setGameLogLoading(false); return; }

      const data = await response.json();
      if (data && data.gameLogs && data.gameLogs.length > 0) {
        setSelectedPlayerData({
          PlayerName: data.playerName,
          Position: data.position,
          Team: data.team,
          teamLogo: teamMap[betRow.homeTeamId]?.logo || teamMap[betRow.awayTeamId]?.logo || '',
          photoUrl: data.photoUrl,
          Height: data.Height,
          Weight: data.Weight,
          Age: data.Age,
          College: data.College,
          Experience: data.Experience,
          EventStart: betRow.eventStart,
          AwayShort: teamMap[betRow.awayTeamId]?.abbreviation || '',
          HomeShort: teamMap[betRow.homeTeamId]?.abbreviation || '',
          AwayLogo: teamMap[betRow.awayTeamId]?.logo || '',
          HomeLogo: teamMap[betRow.homeTeamId]?.logo || '',
        });
        setGameLogData(data.gameLogs);
        setGameLogModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching game log:', error);
    } finally {
      setGameLogLoading(false);
    }
  }, [teamMap]);

  const handleCloseModal = useCallback(() => {
    setGameLogModalOpen(false);
    setSelectedPlayerData(null);
    setGameLogData(null);
  }, []);

  // ══════════════════════════════════════════════════════════
  //  COLUMN RENDERERS
  // ══════════════════════════════════════════════════════════

  const eventCellRenderer = useCallback((params) => {
    const eventStart = params.row.eventStart
      ? new Date(params.row.eventStart).toLocaleString('en-US', {
          year: '2-digit', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true,
        }).replace(',', '')
      : '';
    const league = params.row.league || '';
    const isTennis = league === 'ATP' || league === 'WTA';

    return (
      <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
          {league && (
            <>
              <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0 }}>{league}</Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mx: 0.5 }}>|</Typography>
            </>
          )}
          {isTennis ? (
            <Typography sx={{ fontSize: '0.875rem' }}>{params.row.eventName}</Typography>
          ) : (
            <>
              {teamMap[params.row.awayTeamId]?.logo && (
                <img src={teamMap[params.row.awayTeamId]?.logo} alt="Away" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{teamMap[params.row.awayTeamId]?.abbreviation || teamMap[params.row.awayTeamId]?.name || ''}</Typography>
              <Typography sx={{ fontSize: '0.75rem', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {teamMap[params.row.homeTeamId]?.logo && (
                <img src={teamMap[params.row.homeTeamId]?.logo} alt="Home" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{teamMap[params.row.homeTeamId]?.abbreviation || teamMap[params.row.homeTeamId]?.name || ''}</Typography>
            </>
          )}
        </Box>
        <Typography sx={CELL_STYLES.secondaryText}>{eventStart}</Typography>
      </Box>
    );
  }, [teamMap]);

  const playerCellRenderer = useCallback((params) => {
    const row = params.row;
    const betTypeDisplay = row.betTypeDisplay || '';
    const isPlayerProp = row.marketCategory === 'Player Prop';

    // Top line: player name for props, team/matchup context for game lines
    let topLine = '';
    let isClickable = false;

    if (isPlayerProp) {
      topLine = row.playerName || '';
      isClickable = GAME_LOG_LEAGUES.has(row.league) && row.playerId;
    } else if (row.market === 'point_spread') {
      // Spread: show the full team name for this side
      if (row.side === 'Home') topLine = teamMap[row.homeTeamId]?.name || row.selection || '';
      else if (row.side === 'Away') topLine = teamMap[row.awayTeamId]?.name || row.selection || '';
      else topLine = row.selection || '';
    } else if (row.market === 'total_points' || row.market?.includes('total')) {
      // Total: show abbreviated matchup to save space
      const away = teamMap[row.awayTeamId]?.abbreviation || teamMap[row.awayTeamId]?.name || '';
      const home = teamMap[row.homeTeamId]?.abbreviation || teamMap[row.homeTeamId]?.name || '';
      topLine = `${away}@${home}`;
    } else {
      // Fallback for other game line markets
      topLine = row.selection || row.playerName || '';
    }

    return (
      <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography
          onClick={isClickable ? (e) => { e.stopPropagation(); handlePlayerClick(params.row); } : undefined}
          sx={{
            fontSize: '0.875rem', paddingLeft: '8px',
            textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%',
            cursor: isClickable ? 'pointer' : 'default',
            '&:hover': isClickable ? { textDecoration: 'underline' } : {},
          }}
        >
          {topLine}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', paddingLeft: '8px', color: '#BBBBBB', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
          {betTypeDisplay}
        </Typography>
      </Box>
    );
  }, [handlePlayerClick, teamMap]);

  const logoCellRenderer = useCallback((p) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: 0 }}>
      {bookLogos[p.row.bookName] && (
        <Tooltip title={p.row.bookName || ''} placement="top" arrow>
          <img src={bookLogos[p.row.bookName]} alt={p.row.bookName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} loading="lazy" />
        </Tooltip>
      )}
    </div>
  ), [bookLogos]);

  const priceCellRenderer = useCallback((params) => {
    const price = params.row.price;
    const odds = price == null ? '' : price < 0 ? String(price) : `+${price}`;
    const marketId = params.row.expandMarketId;
    const meta = marketId && marketMeta.get(marketId);
    const isBestOdds = meta && meta.count > 1 &&
      meta.bestOdds === price &&
      meta.bestLine === Number(params.row.points);

    // Build the line portion (e.g. "u1.5", "o222.5", "-6.0", "+15.5")
    const sideLabel = params.row.sideLabel || '';
    const pts = params.row.points;
    let linePart = '';
    if (pts != null) {
      const ptsNum = Number(pts);
      const ptsStr = Number.isInteger(ptsNum) ? `${ptsNum}.0` : String(ptsNum);
      const isOverUnder = sideLabel === 'o' || sideLabel === 'u';
      if (isOverUnder) {
        linePart = `${sideLabel}${ptsStr}`;
      } else {
        // Spreads: show signed number only
        linePart = ptsNum > 0 ? `+${ptsStr}` : ptsStr;
      }
    }

    // Pad to fixed width for alignment (max ~7 chars like "o222.5 " or "+15.5  ")
    const padded = linePart.padStart(7, ' ');

    const r = params.row;
    return (
      <Box sx={{
        ...CELL_STYLES.extendedBackground,
        backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
        cursor: 'pointer',
        '&:hover': { backgroundColor: isBestOdds ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255,255,255,0.05)' },
      }} onClick={() => openOddsHistory({
        fixture_id: r.fixture_id,
        market_id: r.market_id,
        selection: r.selection,
        selection_line: r.selection_line,
        player_id: r.playerId || '',
        points: r.points,
        playerName: r.playerName || r.selection || `${teamMap[r.awayTeamId]?.abbreviation || (r.eventName || '').split(' @ ')[0] || ''} @ ${teamMap[r.homeTeamId]?.abbreviation || (r.eventName || '').split(' @ ')[1] || ''}`,
        marketDisplay: r.marketName || '',
        league: r.league || '',
        awayTeam: teamMap[r.awayTeamId]?.abbreviation || (r.eventName || '').split(' @ ')[0] || '',
        homeTeam: teamMap[r.homeTeamId]?.abbreviation || (r.eventName || '').split(' @ ')[1] || '',
        awayTeamLogo: teamMap[r.awayTeamId]?.logo || '',
        homeTeamLogo: teamMap[r.homeTeamId]?.logo || '',
        eventStart: r.eventStart,
        bookLogo: bookLogos[r.bookName] || '',
      }, r.bookName)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', whiteSpace: 'pre', fontFamily: 'inherit', lineHeight: 1.2 }}>
            {padded} {odds}
          </Typography>
          {showVolume && (() => {
            const limitMax = r.limits?.max;
            if (limitMax == null) return null;
            const display = limitMax >= 1000 ? `$${(limitMax / 1000).toFixed(1).replace(/\.0$/, '')}k` : `$${Math.round(limitMax)}`;
            return (
              <Typography sx={{ color: '#888888', fontSize: '0.65rem', whiteSpace: 'pre', fontFamily: 'inherit', lineHeight: 1 }}>
                {padded.replace(/./g, ' ')} {display}
              </Typography>
            );
          })()}
        </Box>
      </Box>
    );
  }, [marketMeta, openOddsHistory, teamMap, bookLogos, showVolume]);

  const roiCellRenderer = useCallback((p) => <RoiCell value={p.value} />, []);
  const growthCellRenderer = useCallback((p) => <GrowthRateCell value={p.value} />, []);
  const kellyCellRenderer = useCallback((p) => <KellyCell value={p.value} bankroll={bankroll} kellyFraction={kellyFraction} />, [bankroll, kellyFraction]);

  const evAgeCellRenderer = useCallback((params) => {
    return <RoiAgeCell updatedAt={params.row.evUpdatedAt} />;
  }, []);

  const trackCellRenderer = useCallback((p) => (
    <TrackBetCell
      row={p.row} trackedBets={trackedBets} toggleTrack={debouncedToggleTrack}
      trackBet={safeguardedTrackBet} untrackBet={safeguardedUntrackBet}
      trackingInProgress={trackingInProgress} immediatelyTracked={immediatelyTracked || new Set()}
      isAuthenticated={isAuthenticated}
    />
  ), [trackedBets, debouncedToggleTrack, safeguardedTrackBet, safeguardedUntrackBet, trackingInProgress, immediatelyTracked, isAuthenticated]);

  const hideCellRenderer = useCallback((p) => (
    <HideBetCell
      row={p.row} hiddenBets={hiddenBets} setHiddenBets={setHiddenBets}
      toggleHide={toggleHide} showHiddenBets={showHiddenBets}
      isAuthenticated={isAuthenticated} hideMarket={hideMarket} processedBets={processedBets}
    />
  ), [hiddenBets, setHiddenBets, toggleHide, showHiddenBets, isAuthenticated, hideMarket, processedBets]);

  const deeplinkCellRenderer = useCallback((p) => (
    <DeeplinkCell row={p.row} isAuthenticated={isAuthenticated} />
  ), [isAuthenticated]);

  const columnRenderers = useMemo(() => ({
    eventName: eventCellRenderer,
    playerName: playerCellRenderer,
    bookLogo: logoCellRenderer,
    price: priceCellRenderer,
    roi: roiCellRenderer,
    growthRate: growthCellRenderer,
    evUpdatedAt: evAgeCellRenderer,
    kelly: kellyCellRenderer,
    trackBet: trackCellRenderer,
    hideBet: hideCellRenderer,
    deeplink: deeplinkCellRenderer,
  }), [eventCellRenderer, playerCellRenderer, logoCellRenderer, priceCellRenderer, roiCellRenderer, growthCellRenderer, evAgeCellRenderer, kellyCellRenderer, trackCellRenderer, hideCellRenderer, deeplinkCellRenderer]);

  // ══════════════════════════════════════════════════════════
  //  DESKTOP COLUMNS
  // ══════════════════════════════════════════════════════════

  const columns = useMemo(() => {
    return STATIC_COLUMN_DEFINITIONS.map(col => {
      const column = { ...col };

      switch (col.field) {
        case 'price':
          column.renderHeader = HEADER_RENDERERS.withTooltip(col.headerName, 'Highlighted if best odds for the market.');
          break;
        case 'growthRate':
          column.renderHeader = HEADER_RENDERERS.withTooltip(
            col.headerName,
            'If you were to place this bet over and over at the recommended Kelly size, this is the average % that your bankroll will grow for each bet.'
          );
          break;
        case 'evUpdatedAt':
          column.valueGetter = params => params.value || null;
          // fall through
        default:
          if (col.headerName) column.renderHeader = HEADER_RENDERERS.default(col.headerName);
          break;
      }

      if (columnRenderers[col.field]) column.renderCell = columnRenderers[col.field];
      return column;
    });
  }, [columnRenderers]);

  // ══════════════════════════════════════════════════════════
  //  DETAIL PANEL (expand row)
  // ══════════════════════════════════════════════════════════

  // Lazy wrapper — only mounted by DataGridPro when the panel is actually expanded.
  // Reads from refs so getDetailPanelContent can stay stable with [] deps.
  const LazyDetailPanel = ({ row }) => {
    const group = groupedBetsRef.current?.get(row.expandMarketId);
    if (!group || group.length <= 1) return null;

    const mainRowIndex = group.findIndex(bet => bet && bet.id === row.id);

    return (
      <Box sx={{
        p: 0, backgroundColor: '#2a2a2a', borderRadius: '4px',
        overflowX: 'auto', overflowY: 'hidden', display: 'flex', flexWrap: 'nowrap', height: 52,
        '&::-webkit-scrollbar': { height: '7px', backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-track': { backgroundColor: '#333333', border: 'none' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#888888', border: 'none', '&:hover': { backgroundColor: '#aaaaaa' } },
      }}>
        {group.map((bet, index) => {
          if (!bet || !bet.id) return null;
          return (
            <Box key={bet.id || index} sx={{
              width: 150, flexShrink: 0, flexGrow: 0, display: 'flex', alignItems: 'center',
              borderRight: '1px solid #ffffff',
              backgroundColor: index === mainRowIndex ? 'rgba(0, 150, 255, 0.2)' : '#2a2a2a',
            }}>
              <DetailPanelItem index={index} data={{ bets: group }} />
            </Box>
          );
        })}
      </Box>
    );
  };

  const DetailPanelItem = React.memo(({ index, data }) => {
    const bet = data.bets[index];
    const logos = bookLogosRef.current;
    const teams = teamMapRef.current;
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', p: 0, border: 'none', borderRadius: '4px', width: '100%', height: '100%',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
      }} onClick={() => openOddsHistoryRef.current({
        fixture_id: bet.fixture_id,
        market_id: bet.market_id,
        selection: bet.selection,
        selection_line: bet.selection_line,
        player_id: bet.playerId || '',
        points: bet.points,
        playerName: bet.playerName || bet.selection || `${teams[bet.awayTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[0] || ''} @ ${teams[bet.homeTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[1] || ''}`,
        marketDisplay: bet.marketName || '',
        league: bet.league || '',
        awayTeam: teams[bet.awayTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[0] || '',
        homeTeam: teams[bet.homeTeamId]?.abbreviation || (bet.eventName || '').split(' @ ')[1] || '',
        awayTeamLogo: teams[bet.awayTeamId]?.logo || '',
        homeTeamLogo: teams[bet.homeTeamId]?.logo || '',
        eventStart: bet.eventStart,
        bookLogo: logos[bet.bookName] || '',
      }, bet.bookName)}>
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', height: '100%' }}>
          {logos[bet.bookName] ? (
            <Tooltip title={bet.bookName || ''} placement="top" arrow>
              <img src={logos[bet.bookName]} alt={bet.bookName} style={{ width: 'auto', height: '100%', objectFit: 'contain', display: 'block' }} loading="lazy" />
            </Tooltip>
          ) : (
            <Typography sx={{ fontSize: '0.875rem', color: '#ffffff' }}>No Logo</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', ml: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#ffffff', mr: 0.5, whiteSpace: 'nowrap', lineHeight: '44px', fontSize: '0.875rem' }}>
            {bet.sidepoints || ' '}
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff', whiteSpace: 'nowrap', lineHeight: '44px', fontSize: '0.875rem' }}>
            {bet.price == null ? '' : bet.price < 0 ? bet.price : `+${bet.price}`}
          </Typography>
        </Box>
      </Box>
    );
  });

  const getDetailPanelContent = useCallback(({ row }) => {
    if (!row || !row.expandMarketId || !row.marketCount || row.marketCount <= 1) return null;

    // Return a component — DataGridPro only mounts it when the panel is actually
    // expanded. The component reads from groupedBetsRef so this callback is stable.
    return <LazyDetailPanel row={row} />;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDetailPanelHeight = useCallback((params) => {
    return (params?.row?.marketCount > 1) ? 52 : 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDetailPanelExpanded = useCallback((newExpandedRowIds) => {
    let expandedArray;
    if (Array.isArray(newExpandedRowIds)) expandedArray = newExpandedRowIds;
    else if (newExpandedRowIds && typeof newExpandedRowIds[Symbol.iterator] === 'function') expandedArray = Array.from(newExpandedRowIds);
    else expandedArray = [];

    const validRowIds = new Set(displayBets.map(row => row?.id).filter(Boolean));
    const filteredExpandedRowIds = expandedArray.filter(id => {
      if (!id || !validRowIds.has(id)) return false;
      const row = displayBets.find(bet => bet.id === id);
      if (!row || !row.expandMarketId) return false;
      const m = marketMeta.get(row.expandMarketId);
      return m && m.count > 1;
    });
    setExpandedRowIds(new Set(filteredExpandedRowIds));
  }, [displayBets, marketMeta]);

  // ══════════════════════════════════════════════════════════
  //  MOBILE PAGINATION
  // ══════════════════════════════════════════════════════════

  const mobilePageSize = 10;
  const paginatedBets = useMemo(() => {
    const startIdx = currentPage * mobilePageSize;
    const endIdx = startIdx + mobilePageSize;
    return [...displayBets]
      .sort((a, b) => (b.growthRate || 0) - (a.growthRate || 0))
      .slice(startIdx, endIdx);
  }, [displayBets, currentPage]);
  const totalPages = Math.ceil(displayBets.length / mobilePageSize);

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* ═══ MOBILE VIEW ═══ */}
          {screenWidth < 900 && (
            <>
              {/* Mobile Header */}
              <Box sx={{ position: 'sticky', top: '-8px', backgroundColor: '#121212', px: 1.5, pt: 0, pb: 0.75, mt: 0, borderBottom: '1px solid #333', zIndex: 10, minHeight: 95, '& > *:first-of-type': { mt: 0 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ height: '100%', mt: 0, pt: 0.75 }}>
                  <Stack direction="column" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                    {/* First row: League and Category */}
                    <Stack direction="row" spacing={0.5}>
                      {[
                        { label: 'League', value: leagueFilter, set: setLeagueFilter, opts: filterOptions.leagues, id: 'mobile-league' },
                        { label: 'Category', value: categoryFilter, set: setCategoryFilter, opts: filterOptions.categories, id: 'mobile-category' },
                      ].map(({ label, value, set, opts, id }) => (
                        <FormControl key={id} variant="outlined" size="small" sx={{ flex: 1, minWidth: 100, maxWidth: 140 }}>
                          <InputLabel id={`${id}-label`} shrink sx={{ color: '#ffffff', fontSize: '0.875rem' }}>{label}</InputLabel>
                          <Select multiple labelId={`${id}-label`} label={label} notched value={value}
                            onChange={e => handleFilterChange(e, opts, set)}
                            renderValue={sel => renderFilterValue(sel, opts, label)}
                            disabled={!isAuthenticated}
                            sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { paddingRight: '32px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#ffffff' }, '&.Mui-disabled': { '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' } } }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 300 } }, disableAutoFocusItem: true, autoFocus: false }}
                          >
                            <MenuItem value="all"><Checkbox checked={value.length === opts.length} /><ListItemText primary="Select All" /></MenuItem>
                            {opts.map(o => <MenuItem key={o} value={o}><Checkbox checked={value.includes(o)} /><ListItemText primary={o} /></MenuItem>)}
                          </Select>
                        </FormControl>
                      ))}
                    </Stack>
                    {/* Second row: Book and Bet Type */}
                    <Stack direction="row" spacing={0.5}>
                      {[
                        { label: 'Book', value: bookFilter, set: setBookFilter, opts: filterOptions.books, id: 'mobile-book' },
                        { label: 'Bet Type', value: betTypeFilter, set: setBetTypeFilter, opts: filterOptions.betTypes, id: 'mobile-bettype' },
                      ].map(({ label, value, set, opts, id }) => (
                        <FormControl key={id} variant="outlined" size="small" sx={{ flex: 1, minWidth: 100, maxWidth: 140 }}>
                          <InputLabel id={`${id}-label`} shrink sx={{ color: '#ffffff', fontSize: '0.875rem' }}>{label}</InputLabel>
                          <Select multiple labelId={`${id}-label`} label={label} notched value={value}
                            onChange={e => handleFilterChange(e, opts, set)}
                            renderValue={sel => renderFilterValue(sel, opts, label)}
                            disabled={!isAuthenticated}
                            sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { paddingRight: '32px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#ffffff' }, '&.Mui-disabled': { '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' } } }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 300 } }, disableAutoFocusItem: true, autoFocus: false }}
                          >
                            <MenuItem value="all"><Checkbox checked={value.length === opts.length} /><ListItemText primary="Select All" /></MenuItem>
                            {opts.map(o => <MenuItem key={o} value={o}><Checkbox checked={value.includes(o)} /><ListItemText primary={o} /></MenuItem>)}
                          </Select>
                        </FormControl>
                      ))}
                    </Stack>
                  </Stack>
                  <IconButton onClick={handleSettingsOpen} disabled={!isAuthenticated} sx={{ color: '#ffffff', flexShrink: 0, '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}>
                    <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Stack>
              </Box>

              {/* Mobile Content */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, paddingBottom: totalPages > 1 ? '80px' : '16px', position: 'relative' }}>
                {!isAuthenticated && paginatedBets.length > 0 && (
                  <>
                    <Box sx={{ position: 'absolute', top: '220px', left: 0, right: 0, height: '60px', background: 'linear-gradient(to bottom, transparent, rgba(18, 18, 18, 0.8))', zIndex: 999, pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', top: '280px', left: 0, right: 0, bottom: totalPages > 1 ? '64px' : 0, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: 'rgba(18, 18, 18, 0.3)', zIndex: 1000, pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001, width: '90%', maxWidth: 350 }}>
                      <Box sx={{ backgroundColor: '#1e1e1e', border: '2px solid #ff1744', borderRadius: 2, p: 3, textAlign: 'center', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)' }}>
                        <Typography variant="body2" sx={{ color: '#BBBBBB', mb: 3 }}>Please login or subscribe to access all SharpStack features</Typography>
                        <Stack direction="row" spacing={2} justifyContent="center">
                          <Button variant="contained" size="small" sx={{ backgroundColor: '#ff1744', color: '#ffffff', '&:hover': { backgroundColor: '#d50000' } }} onClick={() => window.top.location.href = 'https://www.4for4.com/user/login?destination=sharpstack'}>Log In</Button>
                          <Button variant="contained" size="small" sx={{ backgroundColor: '#ff1744', color: '#ffffff', '&:hover': { backgroundColor: '#d50000' } }} onClick={() => window.top.location.href = 'https://www.4for4.com/plans'}>Subscribe</Button>
                        </Stack>
                      </Box>
                    </Box>
                  </>
                )}
                {paginatedBets.slice(0, isAuthenticated ? undefined : 1).map((bet) => {
                  const meta = bet.expandMarketId && marketMeta.get(bet.expandMarketId);
                  const isBestOdds = meta && meta.count > 1 &&
                    meta.bestOdds === bet.price &&
                    meta.bestLine === Number(bet.points);
                  return (
                    <MobileBetCard
                      key={bet.id} bet={bet} bankroll={bankroll} kellyFraction={kellyFraction}
                      trackedBets={trackedBets} hiddenSet={filterData.hiddenSet} hiddenMarketSet={filterData.hiddenMarketSet}
                      showHiddenBets={showHiddenBets} toggleTrack={debouncedToggleTrack} toggleHide={toggleHide}
                      trackBet={safeguardedTrackBet} untrackBet={safeguardedUntrackBet}
                      trackingInProgress={trackingInProgress} associatedCount={meta ? meta.count : 0}
                      getAssociatedBets={getAssociatedBets}
                      isBestOdds={isBestOdds} immediatelyTracked={immediatelyTracked || new Set()}
                      isAuthenticated={isAuthenticated}
                      bookLogos={bookLogos} teamMap={teamMap}
                      openOddsHistory={openOddsHistory}
                      showVolume={showVolume}
                    />
                  );
                })}
              </Box>

              {/* Mobile Pagination */}
              {totalPages > 1 && (
                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#1e1e1e', borderTop: '1px solid #333', boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.5)', p: 2, zIndex: 1002, flexShrink: 0, opacity: isAuthenticated ? 1 : 0.5, pointerEvents: isAuthenticated ? 'auto' : 'none' }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                    <Button size="small" onClick={() => isAuthenticated && setCurrentPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0 || !isAuthenticated} sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#666666' } }}>Previous</Button>
                    <Typography variant="body2" sx={{ color: isAuthenticated ? '#ffffff' : '#666666' }}>{currentPage + 1} / {totalPages}</Typography>
                    <Button size="small" onClick={() => isAuthenticated && setCurrentPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1 || !isAuthenticated} sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#666666' } }}>Next</Button>
                  </Stack>
                </Box>
              )}
            </>
          )}

          {/* ═══ DESKTOP VIEW ═══ */}
          {screenWidth >= 900 && (
            <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
              {/* Filter Section */}
              <Box className="filter-section" sx={{ flex: 'none', boxSizing: 'border-box' }} ref={filterRef}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    {[
                      { label: 'League', value: leagueFilter, set: setLeagueFilter, opts: filterOptions.leagues, id: 'league' },
                      { label: 'Category', value: categoryFilter, set: setCategoryFilter, opts: filterOptions.categories, id: 'category' },
                      { label: 'Bet Type', value: betTypeFilter, set: setBetTypeFilter, opts: filterOptions.betTypes, id: 'bet-type' },
                      { label: 'Book', value: bookFilter, set: setBookFilter, opts: filterOptions.books, id: 'book' },
                    ].map(({ label, value, set, opts, id }) => (
                      <FormControl key={id} variant="outlined" size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id={`${id}-filter-label`} shrink sx={{ color: '#ffffff', fontSize: '0.875rem' }}>{label}</InputLabel>
                        <Select multiple labelId={`${id}-filter-label`} label={label} notched value={value}
                          onChange={e => handleFilterChange(e, opts, set)}
                          renderValue={sel => renderFilterValue(sel, opts, label)}
                          disabled={!isAuthenticated}
                          sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { color: '#ffffff' }, '&.Mui-disabled': { '& .MuiSelect-select': { color: '#ffffff', WebkitTextFillColor: '#ffffff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' } } }}
                        >
                          <MenuItem value="all"><Checkbox checked={value.length === opts.length} /><ListItemText primary="Select All" sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} /></MenuItem>
                          {opts.map(o => <MenuItem key={o} value={o}><Checkbox checked={value.includes(o)} /><ListItemText primary={o} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} /></MenuItem>)}
                        </Select>
                      </FormControl>
                    ))}
                    <TextField
                      value={searchInput} onChange={handleSearchChange}
                      placeholder="e.g. jokic mgm points" size="small" disabled={!isAuthenticated}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#BBBBBB' }} /></InputAdornment> }}
                      sx={{
                        width: 225, '& .MuiInputBase-input': { color: '#ffffff' },
                        '& .MuiInputBase-input::placeholder': { color: '#BBBBBB', opacity: 1 },
                        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                        '&.MuiTextField-root.Mui-disabled': {
                          '& *': { opacity: '1 !important', '-webkit-text-fill-color': 'unset !important' },
                          '& .MuiInputBase-input::placeholder': { color: '#BBBBBB !important', opacity: '1 !important', '-webkit-text-fill-color': '#BBBBBB !important' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
                        },
                      }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={handleSettingsOpen} disabled={!isAuthenticated} sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}>
                      <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    <IconButton onClick={exportToCSV} disabled={filteredBets.length === 0 || !isAuthenticated}
                      sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' }, '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>

              {/* Table Section */}
              <Box className="table-section">
                <Box className="data-grid-container" ref={gridContainerRef} sx={{ height: '100%', boxSizing: 'border-box' }}>
                  <DataGridPro
                    key={gridKey} apiRef={apiRef} rows={displayBets} columns={columns}
                    pageSize={pageSize} onPageSizeChange={setPageSize} rowsPerPageOptions={[20, 50, 100]}
                    pagination paginationMode="client" page={currentPage} onPageChange={setCurrentPage}
                    sortingMode="client" sortModel={sortModel} onSortModelChange={newModel => setSortModel(newModel)}
                    disableSelectionOnClick disableColumnFilter disableColumnMenu disableColumnResize loading={false}
                    getRowId={row => row.id}
                    getRowClassName={(params) => params.row.isDetail ? 'detail-row' : ''}
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    hideFooterSelectedRowCount
                    sx={{
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1e1e1e', borderBottom: '2px solid #444', position: 'sticky', top: '56px', zIndex: 2 },
                      '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                      '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                      '& .MuiDataGrid-cell': { color: '#ffffff !important', userSelect: 'none', fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.3)' },
                      '& .MuiDataGrid-cell[data-field="bookLogo"]': { padding: 0 },
                      '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                      '& .MuiDataGrid-cell:focus': { outline: 'none' },
                      '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                      '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
                      '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
                      '& .MuiDataGrid-iconButtonContainer': { color: '#ffffff !important', padding: '0px', backgroundColor: 'transparent !important', '&:hover': { backgroundColor: 'transparent !important' } },
                      '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                      '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important', fontSize: '0.875rem' },
                      '& .MuiSelect-icon': { color: '#ffffff !important' },
                      '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                      '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                      '& .MuiDataGrid-main': { padding: 0, boxSizing: 'border-box', overflow: 'hidden' },
                      '& .MuiDataGrid-footerContainer': { backgroundColor: '#1e1e1e', color: '#ffffff !important', position: 'sticky', bottom: 0, zIndex: 1, boxSizing: 'border-box' },
                      ...(!isAuthenticated && {
                        '& .MuiDataGrid-virtualScroller': { overflowY: 'hidden !important', overflowX: 'hidden !important', pointerEvents: 'none', height: '156px', '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 } },
                        '& .MuiDataGrid-virtualScrollerContent': { overflow: 'hidden !important', pointerEvents: 'none', height: '156px' },
                        '& .MuiDataGrid-scrollbar': { display: 'none !important' },
                      }),
                    }}
                    rowBuffer={10} rowThreshold={5}
                  />
                </Box>

                {/* Desktop blur overlay for unauthenticated users */}
                {!isAuthenticated && filteredBets.length > 3 && (
                  <>
                    <Box sx={{ position: 'absolute', top: '212px', left: 0, right: 0, bottom: 0, backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)', backgroundColor: 'rgba(18, 18, 18, 0.35)', zIndex: 11, pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 12 }}>
                      <Box sx={{ backgroundColor: '#1e1e1e', border: '2px solid #ff1744', borderRadius: 2, p: 4, textAlign: 'center', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)' }}>
                        <Typography variant="body1" sx={{ color: '#BBBBBB', mb: 3 }}>Please login or subscribe to access all SharpStack features</Typography>
                        <Stack direction="row" spacing={2} justifyContent="center">
                          <Button variant="contained" sx={{ backgroundColor: '#ff1744', color: '#ffffff', '&:hover': { backgroundColor: '#d50000' } }} onClick={() => window.top.location.href = 'https://www.4for4.com/user/login?destination=sharpstack'}>Log In</Button>
                          <Button variant="contained" sx={{ backgroundColor: '#ff1744', color: '#ffffff', '&:hover': { backgroundColor: '#d50000' } }} onClick={() => window.top.location.href = 'https://www.4for4.com/plans'}>Subscribe</Button>
                        </Stack>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* ═══ SETTINGS POPOVER ═══ */}
          <Popover open={Boolean(settingsAnchorEl)} anchorEl={settingsAnchorEl} onClose={handleSettingsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Box sx={{ p: '12px 16px', width: 320, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small" margin="dense">
                  <InputLabel id="date-range-label" sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Date Range</InputLabel>
                  <Select labelId="date-range-label" label="Date Range" value={dateRange}
                    onChange={e => setDateRange(e.target.value === 'null' ? null : Number(e.target.value))}
                    sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { color: 'white' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' } }}
                  >
                    <MenuItem value={1}>1 Day</MenuItem>
                    <MenuItem value={2}>2 Days</MenuItem>
                    <MenuItem value={7}>7 Days</MenuItem>
                    <MenuItem value={14}>14 Days</MenuItem>
                    <MenuItem value="null">Full Range</MenuItem>
                  </Select>
                </FormControl>
                <TextField notched label="Min EV (%)" type="text" value={minEVDisplay}
                  onChange={e => { const val = e.target.value; setMinEVDisplay(val); if (val === '') setSnipeMinEV(null); else if (val !== '.' && val !== '.0') { const num = parseFloat(val); if (!isNaN(num)) setSnipeMinEV(num); } }}
                  onFocus={e => { if (e.target.value === 'No Min') setMinEVDisplay(''); }}
                  onBlur={e => { const val = e.target.value; if (val === '' || isNaN(parseFloat(val))) { setSnipeMinEV(null); setMinEVDisplay('No Min'); } else { const num = parseFloat(val); setSnipeMinEV(num); setMinEVDisplay(num.toString()); } }}
                  fullWidth size="small" margin="dense" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' } }}
                />
                <TextField notched label="Min Growth (%)" type="text" value={minGrowthDisplay}
                  onChange={e => { const val = e.target.value; setMinGrowthDisplay(val); if (val === '') setMinGrowth(null); else if (val !== '.' && val !== '.0') { const num = parseFloat(val); setMinGrowth(isNaN(num) ? null : num); } }}
                  onFocus={e => { if (e.target.value === 'No Min') setMinGrowthDisplay(''); }}
                  onBlur={e => { const val = e.target.value; if (val === '' || isNaN(parseFloat(val))) { setMinGrowth(null); setMinGrowthDisplay('No Min'); } else { const num = parseFloat(val); setMinGrowth(num); setMinGrowthDisplay(num.toString()); } }}
                  fullWidth size="small" margin="dense" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' } }}
                />
                <TextField notched label="Max EV Age (min)" type="text" value={maxAgeDisplay}
                  onChange={e => { const val = e.target.value; setMaxAgeDisplay(val); if (val === '') setSnipeMaxAge(null); else { const num = parseInt(val); setSnipeMaxAge(isNaN(num) || num < 0 ? null : num); } }}
                  onFocus={e => { if (e.target.value === 'No Max') setMaxAgeDisplay(''); }}
                  onBlur={e => { const val = e.target.value; if (val === '' || isNaN(parseInt(val)) || parseInt(val) < 0) { setSnipeMaxAge(null); setMaxAgeDisplay('No Max'); } else { const num = parseInt(val); setSnipeMaxAge(num); setMaxAgeDisplay(num.toString()); } }}
                  fullWidth size="small" margin="dense" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' } }}
                />
                <TextField notched label="Bankroll" type="text" value={bankroll}
                  onChange={e => { const num = parseFloat(e.target.value); setBankroll(isNaN(num) || num < 0 ? '' : num); }}
                  onBlur={e => { const val = e.target.value; if (val === '' || isNaN(parseFloat(val)) || parseFloat(val) < 0) setBankroll(1000); }}
                  fullWidth size="small" margin="dense" InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography color="white">$</Typography></InputAdornment> }}
                  sx={{ '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' } }}
                />
                <TextField notched label="Kelly Fraction" type="text" value={kellyFraction}
                  onChange={e => setKellyFraction(e.target.value)}
                  onBlur={e => { const num = parseFloat(e.target.value); if (e.target.value === '' || isNaN(num) || num <= 0) setKellyFraction(1); else setKellyFraction(num); }}
                  fullWidth size="small" margin="dense" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff1744' } }}
                />
              </Box>
              <Divider sx={{ borderColor: '#444', mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: '#ffffff', fontSize: '0.875rem', mb: 1 }}>Implied Odds Range (%)</Typography>
                <Slider value={breakEvenWinRange} onChange={handleSliderChange} min={0} max={100} step={1} valueLabelDisplay="auto"
                  marks={[{ value: 0, label: '0' }, { value: 25, label: '25' }, { value: 50, label: '50' }, { value: 75, label: '75' }, { value: 100, label: '100' }]}
                  sx={{ mt: 2, '& .MuiSlider-valueLabel': { fontSize: '0.75rem' }, '& .MuiSlider-markLabel': { fontSize: '0.75rem' } }}
                />
              </Box>
              <Divider sx={{ borderColor: '#444', mb: 2 }} />
              <FormControlLabel
                control={<Checkbox checked={hideTracked} onChange={() => setHideTracked(h => !h)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }} />}
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Hide Tracked Bets</Typography>}
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={<Checkbox checked={showHiddenBets} onChange={() => setShowHiddenBets(h => !h)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }} />}
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Show Hidden Bets</Typography>}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={<Checkbox checked={hideMarket} onChange={() => setHideMarket(h => !h)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Hide Market</Typography>
                    <Tooltip title="When checked, hiding a bet will also hide that bet for all other sportsbooks.">
                      <InfoIcon sx={{ fontSize: '16px', color: '#ffffff', ml: 0.5 }} />
                    </Tooltip>
                  </Box>
                }
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={<Checkbox checked={showVolume} onChange={() => setShowVolume(v => !v)} sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }} />}
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Show Volume</Typography>}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={handleSettingsClose} sx={{ color: '#ffffff', fontSize: '0.875rem', minWidth: '80px', '&:hover': { backgroundColor: '#333333' } }}>Cancel</Button>
                <Button onClick={handleSettingsClose} sx={{ color: '#ff1744', fontSize: '0.875rem', minWidth: '80px', '&:hover': { backgroundColor: '#333333' } }}>Save</Button>
              </Stack>
            </Box>
          </Popover>

          <GameLogModal
            open={gameLogModalOpen} onClose={handleCloseModal}
            playerData={selectedPlayerData} gameLogData={gameLogData}
            loading={gameLogLoading} sport={gameLogSport}
          />
          <OddsHistoryModal
            open={historyOpen}
            onClose={closeOddsHistory}
            data={historyData}
            loading={historyLoading}
            meta={historyMeta}
          />
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default BetsDataGrid;
export { BetsDataGrid as EVTool };